import { KeyboardArrowDown, Language } from "@mui/icons-material";
import {
  AppBar,
  Box,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { matchPath } from "react-router-dom";
import { RootState } from "../../app/store";
import LoaderGlobal from "../molecules/LoaderGlobal";
import { fetchTranslations } from "../../app/slices/GeneralSlice";

const Navbar = () => {
  const dispatch = useDispatch();
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null,
  );
  const { generalSettings, locale, loading } = useSelector(
    (state: RootState) => state.generalSettings,
  );

  if (loading) {
    return <LoaderGlobal />;
  }

  if (Object.keys(generalSettings).length === 0) {
    return null;
  }

  const isRegularBookingProposal =
    matchPath(
      ":restaurantName/em/rb/:id_proposal",
      window.location.pathname,
    ) !== null;

  const stylingType = isRegularBookingProposal
    ? "booking_proposal_styling"
    : "events_styling";

  const styling = generalSettings[stylingType];

  if (!styling) {
    return <LoaderGlobal />;
  }

  const handleCloseLangMenu = () => {
    setAnchorElUser(null);
  };

  const handleOpenLangMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleLangChange = async (lang: string) => {
    dispatch(fetchTranslations(lang) as any);

    handleCloseLangMenu();
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        position="static"
        style={{
          background: styling.navbarBackgroundColor,
          color: styling.navbarTextColor,
          height: 80,
        }}
      >
        <Container disableGutters>
          <Toolbar
            disableGutters
            style={{ justifyContent: "space-between", position: "relative" }}
          >
            {/* Empty left spacer */}
            <Box sx={{ flex: 1 }} />

            {/* Centered logo */}
            <Box
              sx={{
                display: "flex",
                justifyContent:
                  styling.logoPosition === "center" ? "center" : "flex-start",
                position: "absolute",
                width: "100%",
              }}
            >
              {styling.logoUrl && (
                <Typography
                  variant="h6"
                  noWrap
                  component="img"
                  src={styling.logoUrl as string}
                  sx={{
                    fontFamily: "monospace",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    letterSpacing: ".3rem",
                    color: "inherit",
                    textDecoration: "none",
                    maxWidth: 120,
                    maxHeight: 65,
                  }}
                />
              )}
            </Box>

            {/* Right-aligned language selector */}
            <Box
              sx={{
                flexGrow: 0,
                justifyContent: "flex-end",
                display: "flex",
              }}
            >
              <Tooltip title="Open menu for choose language">
                <IconButton onClick={handleOpenLangMenu} sx={{ p: 0 }}>
                  <Typography
                    sx={{
                      textAlign: "center",
                      color: styling.navbarTextColor,
                      display: "flex",
                      gap: 2,
                    }}
                  >
                    <Language />
                    {locale.toLocaleUpperCase()}
                    <KeyboardArrowDown />
                  </Typography>
                </IconButton>
              </Tooltip>
              <Menu
                sx={{ mt: "45px" }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseLangMenu}
              >
                {generalSettings.langs.map((lang) => {
                  return (
                    <MenuItem
                      onClick={() => handleLangChange(lang)}
                      key={`lang-${lang}`}
                    >
                      <Typography sx={{ textAlign: "center" }}>
                        {lang.toLocaleUpperCase()}
                      </Typography>
                    </MenuItem>
                  );
                })}
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
    </Box>
  );
};

export default Navbar;
